import type { KeyboardEventHandler, PointerEventHandler, ReactNode } from "react";
import type { DraggableAttributes } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "../../lib/utils";
import { sortableListSpanClassName, sortableListSpanStyle, type SortableListSpan } from "./columnStyles";

export interface SortableListItemRenderProps {
  /**
   * Spread onto whatever element should start a drag — typically `AnalyticsCard.Toolbar`'s
   * `dragHandleProps`. Drags start ONLY from the element carrying these props, which is what keeps
   * the rest of the item's content clickable.
   */
  handleProps: DraggableAttributes & {
    // Typed as a CALLBACK ref rather than `Ref<HTMLElement>`: the latter is a union including
    // `RefObject<HTMLElement>`, which is NOT assignable to a `Ref<HTMLButtonElement>` prop (such as
    // Button's). A callback taking the wider element type is, so these props spread onto a button.
    ref: (element: HTMLElement | null) => void;
    onPointerDown?: PointerEventHandler<HTMLElement>;
    onKeyDown?: KeyboardEventHandler<HTMLElement>;
  };
  /** True while this item is the one being dragged. */
  isDragging: boolean;
  /** True while any item in the list is being dragged, including this one. */
  isSorting: boolean;
}

export interface SortableListItemProps {
  /** Must match an id in the parent `SortableList`'s `items`. */
  id: string;
  /**
   * How many columns this item occupies, or `"full"` for the whole row. Defaults to one column.
   * Span is the consumer's state — an expand/collapse toolbar action flips it.
   */
  span?: SortableListSpan;
  className?: string;
  /**
   * A render function, not a node: it receives the drag handle props to spread and the live drag
   * state, which is what lets any content be sortable rather than just cards.
   */
  children: (props: SortableListItemRenderProps) => ReactNode;
}

/**
 * One sortable cell of a `SortableList`. Only reachable as `SortableList.Item` — it reads the
 * list's dnd context, so it throws if rendered outside one.
 *
 * It owns the grid cell (position, transform, span) and nothing else: height, padding, and
 * background belong to whatever the render function returns.
 */
export const SortableListItem = ({ id, span, className, children }: SortableListItemProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      data-slot="sortable-list-item"
      data-sortable-id={id}
      data-dragging={isDragging || undefined}
      className={cn(sortableListSpanClassName(span), isDragging && "z-10 opacity-50", className)}
      style={{
        ...sortableListSpanStyle(span),
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {children({
        handleProps: { ...listeners, ...attributes, ref: setActivatorNodeRef },
        isDragging,
        isSorting,
      })}
    </div>
  );
};
